import React, { memo, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import ChevronLeftIcon from 'assets/chevron-left.svg?react';
import AuthForm from 'components/auth/tools/AuthForm';
import { MediaPack } from "components/characters/MediaPackCard/MediaPackCard";
import EmailConfirmationModal from "components/modals/EmailConfirmationModal";
import NiceModal from 'components/modals/NiceModal';
import SubscriptionModal from 'components/modals/SubscriptionModal';
import useModalState from 'hooks/useModalState';
import BuyTokensModal from "modals/BuyTokensModal";
import { useMappedState } from 'redux-react-hook';
import { GUEST_ROUTES } from 'routes/routes';
import styled from 'styled-components';
import { IGame } from "types/games";
import { isBlank } from "utils/storeUtils";

import UserTemplate from '../../components/templates/UserTemplate/UserTemplate';
import PhotoGeneratorModal from "../../modals/PhotoGeneratorModal";
import { useGetCharacterByIdQuery } from "../../services/api/charactersApi";
import { useLazyGetPublicGamesQuery } from "../../services/api/gamesApi";
import CharacterCard from './components/CharacterCard';
import CharacterSkeleton from './components/CharacterSkeleton';
import Gallery from './components/Gallery';
import ScenariosSection from "./components";

const CharacterPage = () => {
  const { characterId } = useParams();

  const navigate = useNavigate();

  const { data: character, isLoading, isFetching, refetch } = useGetCharacterByIdQuery(characterId as string);
  const [getPublicGames, { data: publicGames = [] }] = useLazyGetPublicGamesQuery();

  const [isOpenAuthModal, openAuthModal, closeAuthModal] = useModalState();
  const [isSubscriptionModalOpen, openSubscriptionModal, closeSubscriptionModal] = useModalState();
  const [isOpenPhotoGeneratorModal, openPhotoGeneratorModal, closePhotoGeneratorModal] = useModalState();
  const [isBuyTokensModalOpen, openBuyTokensModal, closeBuyTokensModal] = useModalState();
  const [isEmailConfirmationModalOpen, openEmailConfirmationModal, closeEmailConfirmationModal] = useModalState();

  const [renderForReason, setRenderForReason] = useState<string | null>(null);

  const { hasSubscription, isSignedIn } = useMappedState(state => {
    const currentUser = state.users.usersMap.get(state.users.currentUserId);
    const hasSubscription = currentUser?.hasSubscription;

    return {
      currentUser: state.users.usersMap.get(state.users.currentUserId),
      hasSubscription: hasSubscription,
      isSignedIn: state.users.currentUserId
    };
  });

  const allPacksArePaid = !isBlank(character?.mediaPacks) && character?.mediaPacks?.every(pack => pack.type === 'paid');
  const availableMediaPacks = character?.mediaPacks?.filter((mediaPack) => !!mediaPack.isActive);

  const isVisibleGallery = (!isBlank(availableMediaPacks) && !(allPacksArePaid && !hasSubscription)) || !!character?.hasPhotoGenerator;

  const matchedGames = publicGames.filter(game =>
    character?.scenarios.some(scenario => scenario.gameId === game.id)
  );

  const handleGetDataForCharacterPage = async () => {
    try {
      getPublicGames();
      refetch();
    } catch (error) {
      throw error;
    }
  };

  const handleScenarioCardClick = (game: IGame) => {
    if (!game?.isLocked) {
      navigate(GUEST_ROUTES.ROLEPLAY(game?.alias));
    } else {
      if (isSignedIn) {
        openSubscriptionModal();
      } else {
        openAuthModal();
      }
    }
  };

  const handleCloseSubscriptionModal = () => {
    closeSubscriptionModal();
    setRenderForReason(null);
  };


  const handleOpenSubscriptionModalInGenerator = () => {
    if (!hasSubscription) {
      setRenderForReason('character_photo_generator_click');
      openSubscriptionModal();
    }
  };

  const handleBackButtonClick = () => {
    navigate(`${GUEST_ROUTES.ROOT()}?tab=characters`);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    handleGetDataForCharacterPage();
  }, []);


  if (isLoading || isFetching) {
    return (
      <UserTemplate hasFooter={false} currentName={'character_page'}>
        <CharacterSkeleton />
      </UserTemplate>
    );
  }


  return (
    <UserTemplate hasFooter currentName={'character_page'}>
      <ContentWrapper>
        <BackButton onClick={handleBackButtonClick}>
          <ChevronLeftIcon height={24} width={22} />
        </BackButton>
        {character && (
          <>
            {isMobile && <MobileBackButton onClick={handleBackButtonClick}><ChevronLeftIcon height={24} width={22} />Characters</MobileBackButton>}
            <CharacterCard character={character} />
            {isVisibleGallery && (
              <Gallery
                mediaPacks={(character?.mediaPacks ?? []) as MediaPack[]}
                onCreatePhoto={openPhotoGeneratorModal}
                hasPhotoGenerator={character.hasPhotoGenerator}
                setIsSubscriptionModalOpen={openSubscriptionModal}
                setIsSignUpModalOpen={openAuthModal}
                setIsEmailConfirmationModalOpen={openEmailConfirmationModal}
              />
            )}
          </>
        )
        }
        <ScenariosSection scenarios={matchedGames} onScenarioCardClick={handleScenarioCardClick} characterName={character?.name ?? ''} />
        {/* <ScenarioGeneratorSection onCreateScenarioButtonClick={handleCreateScenarioButtonClick} /> */}
      </ContentWrapper>
      {isOpenAuthModal && (
        <NiceModal onClose={closeAuthModal}>
          <ModalInnerPlaceholder>
            <AuthForm onClose={closeAuthModal} />
          </ModalInnerPlaceholder>
        </NiceModal>
      )}
      {isSubscriptionModalOpen && (
        <SubscriptionModal
          analyticsData={{
            rendered_for_reason: renderForReason,
          }}
          isOpen
          onClose={handleCloseSubscriptionModal}
        />
      )}
      {character && (
        <PhotoGeneratorModal
          isOpen={isOpenPhotoGeneratorModal}
          onClose={closePhotoGeneratorModal}
          character={character}
          refetchCharacter={handleGetDataForCharacterPage}
          onOpenSubscriptionModal={handleOpenSubscriptionModalInGenerator}
          openBuyTokensModal={openBuyTokensModal}
        />
      )}
      {isBuyTokensModalOpen && <BuyTokensModal isOpen onClose={closeBuyTokensModal} analyticsDataRenderReason='generate_character_photo_click' />}
      {isEmailConfirmationModalOpen && (
        <EmailConfirmationModal
          isOpen
          onClose={closeEmailConfirmationModal}
        />
      )}
    </UserTemplate>
  );
};

export default memo(CharacterPage);

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 56px;
  position: relative;
  margin-top: 24px;

  @media (max-width: 720px) {
    gap: 24px;
    margin-top: 0;
  }
`;

const MobileBackButton = styled.div`
  display: flex;
  align-items: center;
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: #A9A9A9;
  cursor: pointer;
  margin: 14px 24px 0 24px;

  @media (max-width: 400px) {
    margin: 0;
  }
`;

const BackButton = styled.div`
  cursor: pointer;
  border-radius: 50%;
  min-width: 40px;
  width: 40px;
  height: 40px;
  background-color: #1D1D1D;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: -50px;
  z-index: 2;
`;

const ModalInnerPlaceholder = styled.div`
  width: 380px;
  padding: 30px;
  box-sizing: border-box;
  @media (max-width: 400px) {
    width: 100%;
  }
`;
